import { useState } from "react";
import { Button, Flex, Text } from "@theorgchartdev/storybook";
import { useTopic } from "@/hooks/useTopic";
import { GetCurrentBoxSelection, GetDoc } from "@/Utils/StateLib";
import { handleCopyToClipboard } from "@/Utils/Misc";
import { FormatFieldValue } from "@coreapp/utils/FormatFieldValue";
import { appstr } from "@coreapp/utils/appstr";
import { GetFunctionalStatementFieldDef } from "@coreapp/utils/GetFunctionalStatementFieldDef";
import "./index.scss";

type FunctionalStatementData = {
  title: string;
  statement: string;
};

function readFunctionalStatementData(): FunctionalStatementData | null {
  const doc = GetDoc();
  if (!doc) return null;

  const selectedBox = GetCurrentBoxSelection();
  const dataObj = selectedBox
    ? selectedBox.GetDataObj()
    : doc.GetSubChartMgr()?.GetCurrentSubChart()?.GetTopDBObj();

  if (!dataObj) return null;

  const objDB = doc.GetObjDB();
  const boxClass = objDB?.GetClassByName("Box");
  if (!boxClass) return null;

  const titleFieldDef = boxClass.GetTitleFieldDef();
  const title: string = titleFieldDef ? (dataObj.GetFieldValue(titleFieldDef) ?? "") : "";

  const statementFieldDef = GetFunctionalStatementFieldDef(boxClass);
  let statement: string = "";
  if (statementFieldDef) {
    const raw = dataObj.GetFieldValue(statementFieldDef);
    if (raw != null && raw !== "") {
      statement = FormatFieldValue(raw, statementFieldDef) ?? "";
      // NIH functional statements use `;` and `:` as line-break delimiters
      statement = statement.replace(/[;:]/g, "\n");
    }
  }

  return { title, statement };
}

export const FunctionalStatementPanel = () => {
  const [, forceRender] = useState(0);

  useTopic("CanvasSelectionChange", () => {
    forceRender(n => n + 1);
  });

  useTopic("subchartSwitch", () => {
    forceRender(n => n + 1);
  });

  const data = readFunctionalStatementData();
  const hasStatement = data && !!data.statement;

  const handleCopy = () => {
    if (!data) return;
    const text = [data.title, data.statement].filter(Boolean).join("\n");
    handleCopyToClipboard(text);
  };

  if (!hasStatement) {
    return (
      <Flex className="functional-statement-panel__empty" direction="column" alignItems="center">
        <Text colorModifier="grey" size="small">
          {appstr("No functional statement")}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="12px" className="functional-statement-panel">
      <Flex justifyContent="space-between" alignItems="flex-start" gap="8px">
        {data.title && (
          <Text modifier="bold" size="medium">
            {data.title}
          </Text>
        )}
        <Button
          iconOnly
          iconProps={{ name: "copy", size: "small" }}
          onClick={handleCopy}
          tooltipProps={{ message: appstr("Copy to clipboard") }}
        />
      </Flex>
      <Text size="small" className="functional-statement-panel__body">
        {data.statement}
      </Text>
    </Flex>
  );
};
